import { Box, Skeleton } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { type GroupedActivityLogEntry, useGroupedActivityLogQuery } from "../../pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { FONT_SIZE, INK, NEUTRAL } from "../../theme/tokens";
import type { NurseInShift } from "../../utils/models";
import { EventList } from "../UIToolkit/molecules/EventList";
import SystemBanner from "../UIToolkit/molecules/SystemBanner";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { ShiftHistoryEntry } from "./ShiftHistoryEntry";
import { ShiftHistoryHeader } from "./ShiftHistoryHeader";
import { ShiftOriginCard } from "./ShiftOriginCard";
import { SHIFT_HISTORY_TEST_ID } from "./shiftHistoryDisplay";

type Props = {
	nurseInShift: NurseInShift;
	nurseName: string;
	onClose: () => void;
	onBackToSchedule?: () => void;
	/** Opens the recurring shift editor; undefined where the surface has no editor or the department locks it. */
	onEditRecurring: (() => void) | undefined;
};

const addedTheShift = (entry: GroupedActivityLogEntry): boolean => entry.shiftOrigin !== null && entry.shiftOrigin !== undefined;

const SKELETON_EVENT_KEYS = ["first", "second", "third"] as const;
const SKELETON_EVENT_HEIGHT_PX = 56;

/** One reader shared by the desktop drawer and the full-screen phone step: the shift's Activity Log, oldest first. */
export const ShiftHistoryContent: FC<Props> = ({ nurseInShift, nurseName, onClose, onBackToSchedule, onEditRecurring }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { data, isPending, isError, refetch } = useGroupedActivityLogQuery(
		{
			shiftId: nurseInShift.id,
			includePunches: false,
			shiftExactTimes: true,
		},
		{ enabled: true },
	);
	const entries = data === undefined ? undefined : [...data].reverse().filter((entry) => !addedTheShift(entry));
	const creatingEntry = data?.find(addedTheShift);
	const isRecurring = Boolean(nurseInShift.recurringShiftId);
	const editRecurring =
		onEditRecurring === undefined
			? undefined
			: () => {
					track("shift_history_edit_recurring_clicked", { shift_id: nurseInShift.id });
					onEditRecurring();
				};

	return (
		<Box data-testid={`${SHIFT_HISTORY_TEST_ID}-content`} sx={{ height: "100%", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
			<ShiftHistoryHeader nurseInShift={nurseInShift} nurseName={nurseName} onClose={onClose} backToSchedule={onBackToSchedule} />
			<Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
				{isPending && (
					<Box data-testid={`${SHIFT_HISTORY_TEST_ID}-loading`} aria-busy="true">
						{SKELETON_EVENT_KEYS.map((key) => (
							<Skeleton key={key} variant="rounded" height={SKELETON_EVENT_HEIGHT_PX} sx={{ mb: 1.5 }} />
						))}
					</Box>
				)}
				{isError && (
					<SystemBanner
						tone="error"
						title={t("shiftHistory.error.title")}
						description={t("shiftHistory.error.hint")}
						action={
							<SystemButton buttonVariant="secondary" size="small" onClick={() => void refetch()} data-testid={`${SHIFT_HISTORY_TEST_ID}-retry`}>
								{t("retry")}
							</SystemButton>
						}
						dataTestId={`${SHIFT_HISTORY_TEST_ID}-error`}
					/>
				)}
				{entries !== undefined && (
					<>
						<ShiftOriginCard
							entry={creatingEntry}
							isRecurring={isRecurring}
							nurseName={nurseName}
							onEditRecurring={editRecurring}
							dataTestId={`${SHIFT_HISTORY_TEST_ID}-origin`}
						/>
						<Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
							<Box component="h3" sx={{ m: 0, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>
								{t("shiftHistory.events.title")}
							</Box>
							<Box component="span" sx={{ color: INK.MUTED, fontSize: FONT_SIZE.CAPTION }}>
								{t("shiftHistory.events.count", { count: entries.length })}
							</Box>
						</Box>
						<Box component="p" sx={{ m: 0, mt: 0.5, mb: 2, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
							{t("shiftHistory.events.coverage")}
						</Box>
						{entries.length === 0 ? (
							<Box
								component="p"
								data-testid={`${SHIFT_HISTORY_TEST_ID}-no-events`}
								sx={{ m: 0, p: 1.5, borderRadius: 1, bgcolor: NEUTRAL[50], fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}
							>
								{t("shiftHistory.events.empty")}
							</Box>
						) : (
							<EventList variant="timeline" dataTestId={`${SHIFT_HISTORY_TEST_ID}-events`}>
								{entries.map((entry, index) => (
									<ShiftHistoryEntry
										key={entry.apiRequestId}
										entry={entry}
										isLatest={index === entries.length - 1}
										dataTestId={`${SHIFT_HISTORY_TEST_ID}-event-${index}`}
									/>
								))}
							</EventList>
						)}
					</>
				)}
			</Box>
		</Box>
	);
};
